/**
 * KinkyMugs cart drawer controller.
 * Handles accessible drawer behavior plus AJAX cart mutations.
 */
class CartDrawer {
  constructor() {
    this.routesRoot = window.Shopify?.routes?.root || '/';
    this.focusableSelector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(', ');

    this.syncElements();

    if (!this.drawer) return;

    this.activeTrigger = null;
    this.lastAnnouncement = '';

    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.handleDocumentChange = this.handleDocumentChange.bind(this);
    this.handleDocumentFocusIn = this.handleDocumentFocusIn.bind(this);
    this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this);
    this.handleDocumentSubmit = this.handleDocumentSubmit.bind(this);
    this.handleCartUpdated = this.handleCartUpdated.bind(this);
    this.handleSectionLoad = this.handleSectionLoad.bind(this);

    document.addEventListener('click', this.handleDocumentClick);
    document.addEventListener('change', this.handleDocumentChange);
    document.addEventListener('focusin', this.handleDocumentFocusIn);
    document.addEventListener('keydown', this.handleDocumentKeydown);
    document.addEventListener('submit', this.handleDocumentSubmit);
    document.addEventListener('cart:updated', this.handleCartUpdated);
    document.addEventListener('shopify:section:load', this.handleSectionLoad);
  }

  syncElements() {
    this.drawer = document.getElementById('CartDrawer');
    this.drawerInner = this.drawer?.querySelector('.drawer__inner') || null;
    this.statusRegion = this.drawer?.querySelector('[data-cart-drawer-status]') || null;
  }

  hasDrawer() {
    if (!(this.drawer instanceof HTMLElement)) {
      this.syncElements();
    }

    return this.drawer instanceof HTMLElement;
  }

  isOpen() {
    return this.hasDrawer() && this.drawer.classList.contains('active');
  }

  isCartLink(target) {
    const link = target.closest('a[href]');

    if (!(link instanceof HTMLAnchorElement)) return false;

    const pathname = new URL(link.href, window.location.origin).pathname;
    return pathname.endsWith('/cart');
  }

  isCartAddForm(form) {
    const pathname = new URL(form.action, window.location.origin).pathname;
    return pathname.endsWith('/cart/add');
  }

  getFocusableElements() {
    if (!this.drawerInner) return [];
    return Array.from(this.drawerInner.querySelectorAll(this.focusableSelector)).filter((element) => {
      return element instanceof HTMLElement && !element.hasAttribute('hidden') && element.offsetParent !== null;
    });
  }

  announce(message) {
    if (!this.statusRegion || !message || this.lastAnnouncement === message) return;

    this.lastAnnouncement = message;
    this.statusRegion.textContent = '';

    window.requestAnimationFrame(() => {
      if (this.statusRegion) {
        this.statusRegion.textContent = message;
      }
    });
  }

  trapFocus(event) {
    const focusableElements = this.getFocusableElements();

    if (focusableElements.length === 0) {
      event.preventDefault();
      this.drawerInner?.focus();
      return;
    }

    const first = focusableElements[0];
    const last = focusableElements[focusableElements.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
      return;
    }

    if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  focusDrawer() {
    if (!this.hasDrawer()) return;

    const focusableElements = this.getFocusableElements();
    const first = focusableElements[0];

    if (first) {
      first.focus();
      return;
    }

    this.drawerInner?.focus();
  }

  toggleBackgroundInteractivity(disable) {
    Array.from(document.body.children).forEach((element) => {
      if (!(element instanceof HTMLElement) || element === this.drawer) return;
      if (this.drawer instanceof HTMLElement && element.contains(this.drawer)) return;
      if (['SCRIPT', 'STYLE', 'NOSCRIPT'].includes(element.tagName)) return;

      if (disable) {
        if (!element.dataset.cartDrawerManaged) {
          element.dataset.cartDrawerManaged = 'true';
          element.dataset.cartDrawerOriginalAriaHidden = element.getAttribute('aria-hidden') ?? '__unset__';
        }

        element.inert = true;
        element.setAttribute('aria-hidden', 'true');
        return;
      }

      if (!element.dataset.cartDrawerManaged) return;

      element.inert = false;

      if (element.dataset.cartDrawerOriginalAriaHidden === '__unset__') {
        element.removeAttribute('aria-hidden');
      } else {
        element.setAttribute('aria-hidden', element.dataset.cartDrawerOriginalAriaHidden);
      }

      delete element.dataset.cartDrawerManaged;
      delete element.dataset.cartDrawerOriginalAriaHidden;
    });
  }

  open(trigger = null) {
    if (!this.hasDrawer()) return;

    if (trigger instanceof HTMLElement) {
      this.activeTrigger = trigger;
    } else if (!(this.activeTrigger instanceof HTMLElement) && document.activeElement instanceof HTMLElement) {
      this.activeTrigger = document.activeElement;
    }

    if (window.kinkyCartDrawerStale === true && !this.isOpen()) {
      window.kinkyCartDrawerStale = false;
      this.refreshDrawer({ open: true }).catch((error) => {
        console.error(error);
        this.open(trigger);
      });
      return;
    }

    this.drawer.classList.add('active');
    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    this.toggleBackgroundInteractivity(true);

    window.requestAnimationFrame(() => this.focusDrawer());
  }

  close() {
    if (!this.hasDrawer()) return;

    this.drawer.classList.remove('active');
    this.drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    this.toggleBackgroundInteractivity(false);

    if (this.activeTrigger instanceof HTMLElement) {
      this.activeTrigger.focus();
    }
  }

  setButtonLoading(button, isLoading, loadingLabel = 'Adding...') {
    if (!(button instanceof HTMLButtonElement) && !(button instanceof HTMLInputElement)) return;

    if (isLoading) {
      if (!button.dataset.originalLabel) {
        button.dataset.originalLabel =
          button instanceof HTMLInputElement ? button.value : button.textContent?.trim() || '';
      }

      button.disabled = true;
      if (button instanceof HTMLInputElement) {
        button.value = loadingLabel;
      } else {
        button.textContent = loadingLabel;
      }
      return;
    }

    button.disabled = false;

    if (button.dataset.originalLabel) {
      if (button instanceof HTMLInputElement) {
        button.value = button.dataset.originalLabel;
      } else {
        button.textContent = button.dataset.originalLabel;
      }
      delete button.dataset.originalLabel;
    }
  }

  async fetchCart() {
    const response = await fetch(`${this.routesRoot}cart.js`, {
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Unable to load the cart.');
    }

    return response.json();
  }

  async fetchCartCount() {
    const cart = await this.fetchCart();
    this.updateCartCount(cart.item_count || 0);
  }

  async addCartItem(variantId, quantity = 1) {
    const formData = new FormData();
    formData.set('id', `${variantId}`);
    formData.set('quantity', `${quantity}`);

    const response = await fetch(`${this.routesRoot}cart/add.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: formData,
    });

    const payload = await response.json();

    if (!response.ok) {
      throw new Error(payload?.description || 'Unable to update cart.');
    }

    return payload;
  }
  async changeCartLine(identifier, quantity, field = 'id') {
    const response = await fetch(`${this.routesRoot}cart/change.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        [field]: identifier,
        quantity,
      }),
    });

    const payload = await response.json();

    if (!response.ok) {
      throw new Error(payload?.description || 'Unable to update cart.');
    }

    return payload;
  }

  async updateCartQuantity(input, quantity) {
    if (!(input instanceof HTMLInputElement) || !this.hasDrawer()) return;

    const lineIndex = Number(input.dataset.index || 0);
    if (!lineIndex) return;

    const nextQuantity = Math.max(0, Number(quantity || 0));
    const wrapper = input.closest('.cart-item__quantity-wrapper');
    const controls = wrapper ? Array.from(wrapper.querySelectorAll('button, input')) : [input];

    controls.forEach((control) => {
      if (control instanceof HTMLButtonElement || control instanceof HTMLInputElement) {
        control.disabled = true;
      }
    });

    try {
      await this.changeCartLine(lineIndex, nextQuantity, 'line');
      const announcement = nextQuantity > 0 ? 'Cart updated.' : 'Item removed from cart.';
      await this.refreshDrawer({ open: true, announcement });
      document.dispatchEvent(new CustomEvent('cart:drawer:updated', { detail: { announcement } }));
    } catch (error) {
      console.error(error);
      this.announce(error.message || 'Unable to update quantity.');
      controls.forEach((control) => {
        if (control instanceof HTMLButtonElement || control instanceof HTMLInputElement) {
          control.disabled = false;
        }
      });
    }
  }

  updateCartCount(itemCount) {
    document.querySelectorAll('[data-cart-drawer-toggle]').forEach((button) => {
      if (!(button instanceof HTMLElement)) return;

      let countBadge = button.querySelector('.header__cart-count');

      if (itemCount > 0) {
        if (!(countBadge instanceof HTMLElement)) {
          countBadge = document.createElement('sup');
          countBadge.className = 'header__cart-count';
          button.prepend(countBadge);
        }

        countBadge.textContent = `${itemCount}`;
        return;
      }

      countBadge?.remove();
    });
  }

  async refreshDrawer({ open = false, announcement = '' } = {}) {
    if (!this.hasDrawer()) return;

    const response = await fetch(`${this.routesRoot}?sections=cart-drawer`, {
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Unable to refresh cart drawer.');
    }

    const sections = await response.json();
    const markup = sections['cart-drawer'];

    if (!markup || !this.drawer) return;

    const parsedHTML = new DOMParser().parseFromString(markup, 'text/html');
    const nextInner = parsedHTML.querySelector('#CartDrawer .drawer__inner') || parsedHTML.querySelector('.drawer__inner');
    if (nextInner) {
      this.drawer.innerHTML = '';
      this.drawer.appendChild(nextInner);
    }

    this.syncElements();
    await this.fetchCartCount();

    if (open) {
      this.open();
    }

    if (announcement) {
      this.announce(announcement);
    }
  }

  async submitCartAddForm(form, submitter) {
    const formData = new FormData(form);

    if (!formData.get('quantity')) {
      formData.set('quantity', '1');
    }

    const response = await fetch(`${this.routesRoot}cart/add.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: formData,
    });

    const payload = await response.json();

    if (!response.ok) {
      throw new Error(payload?.description || 'Unable to add the selected item to cart.');
    }

    const announcement = payload?.title ? `${payload.title} added to cart.` : 'Item added to cart.';
    await this.refreshDrawer({ open: true, announcement });
    this.setButtonLoading(submitter, false);
    document.dispatchEvent(new CustomEvent('cart:drawer:updated', { detail: payload }));
  }

  async addUpsell(button) {
    if (!(button instanceof HTMLButtonElement) || !this.hasDrawer()) return;

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    this.setButtonLoading(button, true);

    try {
      const formData = new FormData();
      formData.set('id', variantId);
      formData.set('quantity', '1');

      const response = await fetch(`${this.routesRoot}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      });

      const payload = await response.json();

      if (!response.ok) {
        throw new Error(payload?.description || 'Unable to add upsell item.');
      }

      const announcement = payload?.title ? `${payload.title} added to cart.` : 'Item added to cart.';
      await this.refreshDrawer({ open: true, announcement });
      document.dispatchEvent(new CustomEvent('cart:drawer:updated', { detail: payload }));
    } catch (error) {
      console.error(error);
      this.announce(error.message || 'Unable to update cart.');
      this.setButtonLoading(button, false);
    }
  }

  handleDocumentClick(event) {
    const trigger = event.target instanceof Element ? event.target : null;
    if (!trigger) return;

    const cartToggle = trigger.closest('[data-cart-drawer-toggle]');
    if (cartToggle instanceof HTMLElement) {
      event.preventDefault();
      this.open(cartToggle);
      return;
    }

    if (this.isCartLink(trigger)) {
      event.preventDefault();
      this.open(trigger.closest('a[href]'));
      return;
    }

    const upsellButton = trigger.closest('[data-cart-upsell-add]');
    if (upsellButton instanceof HTMLButtonElement) {
      event.preventDefault();
      this.addUpsell(upsellButton);
      return;
    }

    const removeButton = trigger.closest('[data-cart-quantity-remove]');
    if (removeButton instanceof HTMLButtonElement && this.isOpen()) {
      event.preventDefault();
      event.stopImmediatePropagation();

      const lineIndex = Number(removeButton.dataset.index || 0);
      if (!lineIndex) return;

      const item = removeButton.closest('.cart-item');
      const input = item?.querySelector('[data-cart-quantity-input]');
      if (input instanceof HTMLInputElement) {
        input.value = '0';
        this.updateCartQuantity(input, 0).catch((error) => {
          console.error(error);
        });
      }
      return;
    }

    const quantityButton = trigger.closest('[data-cart-quantity-button]');
    if (quantityButton instanceof HTMLButtonElement && this.isOpen()) {
      event.preventDefault();
      event.stopImmediatePropagation();

      const input = quantityButton.parentElement?.querySelector('[data-cart-quantity-input]');
      if (!(input instanceof HTMLInputElement)) return;

      const currentValue = Number(input.value || 0);
      const nextValue = quantityButton.name === 'minus' ? Math.max(0, currentValue - 1) : currentValue + 1;
      input.value = `${nextValue}`;
      this.updateCartQuantity(input, nextValue).catch((error) => {
        console.error(error);
      });
      return;
    }

    if (!this.isOpen()) return;

    if (trigger.closest('[data-cart-drawer-close]')) {
      event.preventDefault();
      this.close();
      return;
    }

    if (trigger === this.drawer) {
      this.close();
    }
  }

  handleDocumentChange(event) {
    const target = event.target;

    if (target instanceof HTMLInputElement && target.matches('[data-cart-quantity-input]')) {
      const nextValue = Math.max(0, Number(target.value || 0));
      target.value = `${nextValue}`;
      this.updateCartQuantity(target, nextValue).catch((error) => {
        console.error(error);
      });
    }
  }

  handleDocumentFocusIn(event) {
    if (!this.isOpen() || !this.drawerInner) return;

    const focusTarget = event.target;
    if (focusTarget instanceof Node && !this.drawerInner.contains(focusTarget)) {
      this.focusDrawer();
    }
  }

  handleDocumentKeydown(event) {
    if (!this.isOpen()) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key === 'Tab') {
      this.trapFocus(event);
    }
  }

  handleDocumentSubmit(event) {
    if (!this.hasDrawer()) return;

    const form = event.target;

    if (!(form instanceof HTMLFormElement) || !this.isCartAddForm(form)) {
      return;
    }

    if (
      event.submitter instanceof Element &&
      event.submitter.closest('[data-product-dynamic-checkout], .shopify-payment-button')
    ) {
      return;
    }

    event.preventDefault();

    const submitter =
      event.submitter instanceof HTMLButtonElement
        ? event.submitter
        : form.querySelector('button[type="submit"], input[type="submit"]');

    this.setButtonLoading(submitter, true);

    this.submitCartAddForm(form, submitter).catch((error) => {
      console.error(error);
      this.announce(error.message || 'Unable to update cart.');
      this.setButtonLoading(submitter, false);
    });
  }

  handleCartUpdated(event) {
    if (!this.hasDrawer()) return;

    const announcement = event.detail?.announcement || 'Cart updated.';

    this.refreshDrawer({ open: true, announcement }).catch((error) => {
      console.error(error);
    });
  }

  handleSectionLoad(event) {
    if (!(event.target instanceof Element)) return;

    if (event.target.querySelector('#CartDrawer') || event.target.id === 'CartDrawer' || event.target.closest('#shopify-section-cart-drawer')) {
      this.syncElements();
      if (this.drawer && this.isOpen()) {
        this.toggleBackgroundInteractivity(true);
      }
    }
  }
}

window.cartDrawer = new CartDrawer();
window.kinkyCart = {
  open(trigger) {
    if (window.cartDrawer && typeof window.cartDrawer.open === 'function') {
      window.cartDrawer.open(trigger);
    }
  },

  close() {
    if (window.cartDrawer && typeof window.cartDrawer.close === 'function') {
      window.cartDrawer.close();
    }
  },

  add(event, form) {
    if (!(form instanceof HTMLFormElement)) return true;

    const drawer = window.cartDrawer;
    if (!drawer || typeof drawer.isCartAddForm !== 'function' || !drawer.isCartAddForm(form)) {
      return true;
    }

    if (event) {
      event.preventDefault();
      event.stopPropagation();

      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }
    }

    const submitter =
      event && (event.submitter instanceof HTMLButtonElement || event.submitter instanceof HTMLInputElement)
        ? event.submitter
        : form.querySelector('button[type="submit"], input[type="submit"]');

    drawer.setButtonLoading(submitter, true);

    drawer.submitCartAddForm(form, submitter).catch((error) => {
      console.error(error);
      drawer.announce(error.message || 'Unable to update cart.');
      drawer.setButtonLoading(submitter, false);
    });

    return false;
  },
};
